import React, { useState, useEffect } from 'react';
import './DynamicForm.css';

/**
 * DynamicForm - Formulario dinámico configurable desde JSON
 * Soporta campos obligatorios, opcionales, validación y lógica condicional
 */
const DynamicForm = ({ formConfig }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [conditionalMessages, setConditionalMessages] = useState({});
  const [dismissedStickyButton, setDismissedStickyButton] = useState(false);

  const { title, subtitle, fields, submitText = 'Enviar', successMessage } = formConfig;

  // Verificar si todos los campos obligatorios están completos
  useEffect(() => {
    const requiredFields = fields.filter(f => f.required);
    const allRequiredFilled = requiredFields.every(field => {
      const value = formData[field.name];
      return value && value.toString().trim() !== '';
    });
    setShowSkipButton(allRequiredFilled);
  }, [formData, fields]);

  // Manejar cambios en los campos
  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Resetear el dismissed para que el sticky button vuelva a aparecer
    setDismissedStickyButton(false);

    // Limpiar error si existe
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }

    // Verificar lógica condicional
    const field = fields.find(f => f.name === fieldName);
    if (field?.conditionalLogic) {
      const logic = field.conditionalLogic[value];
      if (logic) {
        setConditionalMessages(prev => ({
          ...prev,
          [fieldName]: logic.message
        }));
      } else {
        setConditionalMessages(prev => ({
          ...prev,
          [fieldName]: null
        }));
      }
    }
  };

  // Validar campo individual
  const validateField = (field) => {
    const value = formData[field.name];

    if (field.required && (!value || value.toString().trim() === '')) {
      return `${field.label} es obligatorio`;
    }

    if (value && field.validation) {
      const { type, pattern, message } = field.validation;

      if (type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return message || 'Email inválido';
        }
      }

      if (type === 'phone') {
        const phoneRegex = /^[\d\s+\-()+]+$/;
        if (!phoneRegex.test(value)) {
          return message || 'Teléfono inválido';
        }
      }

      if (pattern) {
        const regex = new RegExp(pattern);
        if (!regex.test(value)) {
          return message || 'Formato inválido';
        }
      }
    }

    return null;
  };

  // Validar formulario completo
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = fields.filter(f => f.required);

    requiredFields.forEach(field => {
      const error = validateField(field);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll al primer error
      const firstError = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstError}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Aquí se conectaría a la base de datos
    console.log('Form submitted:', formData);
    setSubmitted(true);

    // TODO: Enviar a la base de datos
    // await submitToDatabase(formData);
  };

  // Saltar a campos opcionales
  const skipToEnd = () => {
    const lastOptionalIndex = fields.findIndex(f => !f.required && fields.indexOf(f) > currentStep);
    if (lastOptionalIndex !== -1) {
      setCurrentStep(lastOptionalIndex);
    }
  };

  // Scroll al botón de enviar y ocultar sticky button
  const scrollToSubmit = () => {
    setDismissedStickyButton(true);
    const submitButton = document.querySelector('.submit-button');
    if (submitButton) {
      submitButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Renderizar campo según tipo
  const renderField = (field, index) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    const conditionalMessage = conditionalMessages[field.name];

    return (
      <div key={field.name} className={`form-field ${error ? 'has-error' : ''}`}>
        <label htmlFor={field.name} className="form-label">
          {field.label}
          {field.required && <span className="required-mark">*</span>}
        </label>

        {field.type === 'text' || field.type === 'email' || field.type === 'tel' ? (
          <input
            type={field.type}
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="form-input"
          />
        ) : field.type === 'textarea' ? (
          <textarea
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="form-textarea"
            rows={field.rows || 4}
          />
        ) : field.type === 'select' ? (
          <select
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className="form-select"
          >
            <option value="">Seleccioná una opción</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field.type === 'radio' ? (
          <div className="form-radio-group">
            {field.options?.map(option => (
              <label key={option.value} className="form-radio-label">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  className="form-radio"
                />
                <span className="form-radio-text">{option.label}</span>
              </label>
            ))}
          </div>
        ) : null}

        {error && <span className="form-error">{error}</span>}
        {field.helper && <span className="form-helper">{field.helper}</span>}

        {conditionalMessage && (
          <div className="conditional-message">
            {conditionalMessage}
          </div>
        )}
      </div>
    );
  };

  if (submitted) {
    return (
      <div className="dynamic-form-container">
        <div className="form-success">
          <div className="success-icon">✓</div>
          <h3 className="success-title">{successMessage?.title || '¡Gracias por contactarnos!'}</h3>
          <p className="success-message">
            {successMessage?.message || 'Te contactaremos pronto para hablar sobre tu proyecto.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dynamic-form-container">
      <div className="form-header">
        {title && <h2 className="form-title">{title}</h2>}
        {subtitle && <p className="form-subtitle">{subtitle}</p>}
      </div>

      <form onSubmit={handleSubmit} className="dynamic-form">
        <div className="form-fields">
          {fields.map((field, index) => renderField(field, index))}
        </div>

        <div className="form-actions">
          {showSkipButton && (
            <button
              type="button"
              onClick={skipToEnd}
              className="skip-button"
            >
              Saltar campos opcionales →
            </button>
          )}

          <button type="submit" className="submit-button pillButton pillButton--on-dark">
            {submitText}
          </button>
        </div>
      </form>

      {/* Botón sticky flotante */}
      {showSkipButton && !dismissedStickyButton && (
        <button
          type="button"
          onClick={scrollToSubmit}
          className="sticky-skip-button"
          aria-label="Ir al final del formulario"
        >
          <span className="sticky-skip-icon">↓</span>
          <span className="sticky-skip-text">Ir al envío</span>
        </button>
      )}
    </div>
  );
};

export default DynamicForm;
